import { Fragment, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  AutoAwesome as AiIcon,
  Lightbulb as HintIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api, { getPastPaperApiPrefix, getPastPaperPortalHomePath, getPastPaperResultPath } from "../../../api";
import HtmlWithLatex from "../../QuestionBank/components/HtmlWithLatex";
import { dropPrintedDiagramFromSketchPart, reconcileRowFiguresForDisplay } from "../../../utils/groupedQuestionFigures";
import { reviewYourAnswerWasCorrectLabel } from "../utils/reviewEmbeddedCorrectAnswer";
import VideoExplanationPlayer from "../../../components/VideoExplanationPlayer";
import {
  stripAnswerBrackets,
  statedAreaWhenQuestionAsksForArea,
  completeTruncatedMixedCorrectAnswer,
  singleValueMatches,
  isAnswerCorrect,
  isAnswerPartial,
  numericListPartialCredit,
  arrowSequencePositionalCredit,
  decodeHtmlEntities,
  submissionIsDeterministicallyWrong,
  submittedMissesPercentSign,
  splitMultiValueAnswer,
  fillBlanksAreInterchangeable,
  commutedCalculationMatches,
} from "../../../utils/answerMatchUtils";
import QuestionPaperLayout from "../../QuestionBank/components/QuestionPaperLayout";
import ImageOverlayFillInput from "../components/ImageOverlayFillInput";
import InteractiveDiagramCanvas from "../components/InteractiveDiagramCanvas";
import { interactiveRevealValue } from "../utils/interactiveRevealValue";
import { interactiveDeclaredNone, interactiveReviewValueOf } from "../utils/interactiveAnswerValue";
import { sharedGridGivenPointsGuide, sharedGridOriginalShapeGuide } from "../utils/sharedGridGuides";
import {
  coerceFillAnswersArray,
  parsePipeJoinedBlanks,
  isFillQuestionType,
} from "../../../utils/fillQuestionUtils";
import { resolveLeafVerdict } from "../utils/leafMarkVerdict";
import { extractSubCore } from "../utils/groupedQuestionUtils";
import { formatReviewCorrectLabel } from "../utils/breakdownFormat";
import MarksBreakdownTable from "../components/MarksBreakdownTable";

const PERFORMANCE_LEVEL_CONFIG = {
  excellent: { label: "Excellent", bgcolor: "#dcfce7", color: "#166534" },
  good: { label: "Good", bgcolor: "#dbeafe", color: "#1e40af" },
  average: { label: "Average", bgcolor: "#fef9c3", color: "#854d0e" },
  needs_improvement: { label: "Needs Improvement", bgcolor: "#fee2e2", color: "#991b1b" },
  unknown: { label: "Unknown", bgcolor: "#f3f4f6", color: "#374151" },
};

const AiInsightsTab = ({ aiFeedbackStatus, aiOverall, aiScore, aiPercentage, totalMarks, ruleScore }) => {
  if (aiFeedbackStatus === "pending" || aiFeedbackStatus === "processing") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 2 }}>
        <CircularProgress size={40} sx={{ color: "#7c3aed" }} />
        <Typography variant="body1" sx={{ color: "#7c3aed", fontWeight: 500 }}>
          AI is analysing your answers…
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This usually takes 5–15 seconds. The page will update automatically.
        </Typography>
      </Box>
    );
  }

  if (aiFeedbackStatus === "failed" || !aiOverall) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        AI feedback is not available for this attempt. This may be due to a temporary issue.
      </Alert>
    );
  }

  const perfConfig = PERFORMANCE_LEVEL_CONFIG[aiOverall.performance_level] ?? PERFORMANCE_LEVEL_CONFIG.unknown;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Card variant="outlined" sx={{ borderColor: "#e0d7ff", bgcolor: "#f3f0ff" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <AiIcon sx={{ color: "#7c3aed", fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#7c3aed" }}>
                AI-Adjusted Score
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: "#7c3aed", mb: 0.5 }}>
              {Number(aiScore ?? 0).toFixed(1)}
              <Typography component="span" variant="h6" sx={{ color: "text.secondary", fontWeight: 400 }}>
                {" "}/ {Number(totalMarks ?? 0).toFixed(1)}
              </Typography>
            </Typography>
            <Typography variant="body2" sx={{ color: "#7c3aed", fontWeight: 600 }}>
              {Number(aiPercentage ?? 0).toFixed(1)}%
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Based on semantic answer matching
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <TrendingUpIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Performance Level
              </Typography>
            </Box>
            <Chip
              label={perfConfig.label}
              sx={{ bgcolor: perfConfig.bgcolor, color: perfConfig.color, fontWeight: 700, fontSize: "1rem", height: 36, px: 1, mb: 1 }}
            />
            {ruleScore != null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                Auto Score: {Number(ruleScore).toFixed(1)} / {Number(totalMarks ?? 0).toFixed(1)}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {aiOverall.strengths?.length > 0 && (
          <Card variant="outlined" sx={{ borderColor: "#bbf7d0" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#166534" }}>
                  Strengths
                </Typography>
              </Box>
              {aiOverall.strengths.map((s, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#16a34a", mt: 0.7, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{s}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
        {aiOverall.weaknesses?.length > 0 && (
          <Card variant="outlined" sx={{ borderColor: "#fecaca" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <CancelIcon sx={{ color: "#dc2626", fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#991b1b" }}>
                  Areas to Improve
                </Typography>
              </Box>
              {aiOverall.weaknesses.map((w, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#dc2626", mt: 0.7, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{w}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
      </Box>

      {aiOverall.recommendations?.length > 0 && (
        <Card variant="outlined" sx={{ borderColor: "#bfdbfe" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <HintIcon sx={{ color: "#2563eb", fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e40af" }}>
                Study Recommendations
              </Typography>
            </Box>
            {aiOverall.recommendations.map((r, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
                <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: "#dbeafe", color: "#1e40af", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0, mt: 0.1 }}>
                  {i + 1}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{r}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

// ── AI Feedback Panel ─────────────────────────────────────────────────────────
const AiFeedbackPanel = ({ questionId, aiFeedbackMap, aiFeedbackStatus, questionMarks, ruleIsCorrect, ruleIsPartial, forceIncorrect = false }) => {
  // Null map = the caller opted this row out of AI entirely (exact_match); not even the
  // "generating…" placeholder belongs here, because no AI verdict is ever coming.
  if (!aiFeedbackMap) return null;

  const aiq = aiFeedbackMap?.[String(questionId)];

  if (aiFeedbackStatus === "pending" || aiFeedbackStatus === "processing") {
    return (
      <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: "#f3f0ff", border: "1px solid #e0d7ff", display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={14} sx={{ color: "#7c3aed" }} />
        <Typography variant="caption" sx={{ color: "#7c3aed", fontStyle: "italic" }}>
          AI feedback is being generated…
        </Typography>
      </Box>
    );
  }

  if (aiFeedbackStatus === "failed" || !aiq) return null;

  const aiIsCorrect = aiq.is_correct;
  const partialScore = aiq.partial_score;
  const isAiPartial = partialScore != null && partialScore > 0 && partialScore < 1;

  const aiConflictsWithRuleCorrect = !forceIncorrect && ruleIsCorrect === true && aiIsCorrect !== true;
  // The marks say partial and the stored AI verdict says otherwise — either way round. A stored AI
  // "Correct" over a half answer (the working without "Shape B", St Anselm's 2024 Q15c) printed a green
  // "AI FEEDBACK Correct" beside the "Partially correct" mark.
  const aiConflictsWithRulePartial = !forceIncorrect && ruleIsPartial === true && ruleIsCorrect !== true
    && (aiIsCorrect === false || aiIsCorrect === true) && !isAiPartial;
  const anyConflict = aiConflictsWithRuleCorrect || aiConflictsWithRulePartial;

  let effectiveCorrect = forceIncorrect ? false : aiIsCorrect;
  let effectivePartial = forceIncorrect ? false : isAiPartial;

  if (aiConflictsWithRuleCorrect) {
    effectiveCorrect = true;
    effectivePartial = false;
  } else if (aiConflictsWithRulePartial) {
    effectiveCorrect = false;
    effectivePartial = true;
  }

  const earnedMarks = partialScore != null
    ? Math.round((partialScore * (questionMarks ?? 1)) * 100) / 100
    : null;

  let bgcolor = "#f8f9fa"; let borderColor = "#e0e0e0";
  if (effectiveCorrect === true && !effectivePartial) { bgcolor = "#f0fdf4"; borderColor = "#bbf7d0"; }
  else if (effectivePartial) { bgcolor = "#fffbeb"; borderColor = "#fde68a"; }
  else if (effectiveCorrect === false) { bgcolor = "#fff5f5"; borderColor = "#fecaca"; }

  return (
    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor, border: "1px solid", borderColor }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75, flexWrap: "wrap" }}>
        <AiIcon sx={{ fontSize: 15, color: "#7c3aed" }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: 0.5 }}>
          AI Feedback
        </Typography>
        {effectiveCorrect === true && !effectivePartial && (
          <Chip icon={<CheckCircleIcon sx={{ fontSize: 13 }} />} label="Correct" size="small"
            sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 600, height: 20, fontSize: "0.7rem" }} />
        )}
        {effectivePartial && (
          <Chip
            label={
              aiConflictsWithRulePartial
                ? "Partially correct"
                : `Partial — ${earnedMarks != null ? earnedMarks : Math.round(partialScore * 100) + "%"} marks`
            }
            size="small"
            sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 600, height: 20, fontSize: "0.7rem" }}
          />
        )}
        {effectiveCorrect === false && !effectivePartial && (
          <Chip icon={<CancelIcon sx={{ fontSize: 13 }} />} label="Incorrect" size="small"
            sx={{ bgcolor: "#fee2e2", color: "#991b1b", fontWeight: 600, height: 20, fontSize: "0.7rem" }} />
        )}
        {aiConflictsWithRuleCorrect && (
          <Chip label="Re-evaluated: Correct" size="small"
            sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 600, height: 20, fontSize: "0.7rem" }} />
        )}
        {aiConflictsWithRulePartial && (
          <Chip label="Re-evaluated: Partially correct" size="small"
            sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 600, height: 20, fontSize: "0.7rem" }} />
        )}
      </Box>
      {aiConflictsWithRuleCorrect ? (
        <Typography variant="body2" sx={{ color: "#166534", lineHeight: 1.6 }}>
          Correct.
        </Typography>
      ) : aiConflictsWithRulePartial ? (
        <Typography variant="body2" sx={{ color: "#92400e", lineHeight: 1.6 }}>
          {aiIsCorrect === true
            ? "Partially correct — part of the full answer is missing. Compare with the correct answer."
            : "Partially correct — your answer matches a required value."}
        </Typography>
      ) : forceIncorrect && aiIsCorrect === true ? (
        // The stored sentence is the AI's "Correct." — printing it under an Incorrect chip contradicts it.
        <Typography variant="body2" sx={{ color: "#991b1b", lineHeight: 1.6 }}>
          Incorrect — compare your answer with the correct answer.
        </Typography>
      ) : aiq.feedback ? (
        <Box sx={{ fontSize: "0.875rem", color: "text.primary", lineHeight: 1.6, mb: aiq.hint ? 0.75 : 0 }}>
          <HtmlWithLatex html={aiq.feedback} />
        </Box>
      ) : null}
      {aiq.hint && !anyConflict && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.5 }}>
          <HintIcon sx={{ fontSize: 15, color: "#d97706", mt: 0.2, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: "#92400e", fontStyle: "italic", lineHeight: 1.5 }}>
            <strong>Tip:</strong> {aiq.hint}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const SUB_LETTERS = "abcdefghijklmnopqrstuvwxyz".split("");

// A fixed-total group splits its mark across its parts (10 over 7 → 1.428571…), so the raw value can't
// go straight into a chip.
const fmtMarks = (n) => {
  const num = Number(n ?? 0);
  return Number.isInteger(num) ? String(num) : String(Math.round(num * 100) / 100);
};

const parseReviewLabel = (label) => {
  const m = String(label || "").match(/^Q(\d+)([a-z])?$/i);
  if (!m) return { questionNumber: undefined, subLabel: undefined };
  return { questionNumber: Number(m[1]), subLabel: m[2] || undefined };
};

const stripEmbeddedTableHtml = (text) =>
  String(text || "").replace(/<table[\s\S]*?<\/table>/gi, "").trim();

const collectRowImages = (row) => {
  const arr = Array.isArray(row?.image_urls) ? row.image_urls.filter(Boolean) : [];
  if (arr.length) return arr;
  return row?.image_url ? [row.image_url] : [];
};

// A matching answer is a set of links, so review it link by link: the student needs to see WHICH pair
// they got wrong, not one "a → 1, b → 2" string to diff by eye. Rows come from the server
// (row.matching_review), already paired up with the expected right-hand item.
const MatchingReview = ({ rows }) => (
  <Box sx={{ mb: 1.5 }}>
    {rows.map((pair, i) => (
      <Box
        key={i}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr 1fr" },
          alignItems: "center",
          gap: 1,
          px: 1.25,
          py: 1,
          borderRadius: 1,
          mb: 0.75,
          border: "1px solid",
          borderColor: pair.is_correct ? "#bbf7d0" : "#fecaca",
          bgcolor: pair.is_correct ? "#f0fdf4" : "#fff5f5",
        }}
      >
        <Box sx={{ fontSize: "0.875rem", fontWeight: 600 }}><HtmlWithLatex html={String(pair.left ?? "")} /></Box>
        <Typography sx={{ color: "text.disabled", display: { xs: "none", sm: "block" } }}>→</Typography>
        <Box sx={{ fontSize: "0.875rem", color: pair.is_correct ? "success.main" : "error.main", fontWeight: 700 }}>
          {pair.your ? <HtmlWithLatex html={String(pair.your)} /> : <Typography variant="body2" color="text.disabled">— not answered</Typography>}
        </Box>
        {!pair.is_correct && (
          <Box sx={{ fontSize: "0.8rem", color: "success.dark" }}>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>Correct:</Typography>
            <HtmlWithLatex html={String(pair.correct ?? "")} />
          </Box>
        )}
      </Box>
    ))}
  </Box>
);

// ── Essay / multi-option correct-answer formatting (parity with StudentPaperTake) ─
const ESSAY_SENTENCE_START =
  "The|A|An|At|In|On|It|He|She|They|There|His|Her|Before|After|During|Once|When|While|Even|As|For|From|With|Without|Suddenly|Then|Soon|Later|Meanwhile";

const repairEssayCorrectAnswerHtml = (raw) => {
  let s = String(raw ?? "");
  if (!s.trim()) return s;
  s = s.replace(/([.!?…])\s*(\d{1,2}\s*[).]\s+)/g, "$1</p><p>$2");
  s = s.replace(/([.!?…])(\d{1,2}\s*[).]\s+)/g, "$1</p><p>$2");
  s = s.replace(
    new RegExp(
      `(<(?:strong|b)\\b[^>]*>\\s*\\d{1,2}\\s*[)\\.]\\s*)([^<]{1,100}?)(<\\/(?:strong|b)>)\\s*(${ESSAY_SENTENCE_START})\\b`,
      "gi"
    ),
    "$1$2$3</p><p>$4"
  );
  s = s.replace(
    new RegExp(`(<\\/(?:strong|b|span|em)>)\\s*(${ESSAY_SENTENCE_START})\\b`, "g"),
    "$1</p><p>$2"
  );
  s = s.replace(/(Answer\s+\d+\s*:)/gi, "</p><p><strong>$1</strong> ");
  if (/<\/p>\s*<p>/i.test(s) && !/^</.test(s.trim())) s = `<p>${s}</p>`;
  return s;
};

const looksLikeLongEssayModelAnswer = (raw) => {
  const s = String(raw ?? "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (s.length < 60) return false;
  const words = s.split(/\s+/).filter(Boolean);
  return (
    /\b1\s*[).]\s*\S[\s\S]{20,}?\b2\s*[).]\s*\S/.test(s) ||
    (s.match(/\bAnswer\s+[1-9]\s*:/gi) || []).length >= 2 ||
    (s.match(/\bOption\s+[1-9]\b/gi) || []).length >= 2 ||
    (words.length >= 8 && /\b(?:because|therefore|hence|so that|cannot be|must be|impossible)\b/i.test(s))
  );
};

const formatEssayTopicPartsAsHtml = (parts) => {
  const cleaned = (parts || []).map((p) => String(p ?? "").trim()).filter(Boolean);
  if (cleaned.length < 2) return null;
  const plains = cleaned.map((p) => p.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim());
  if (!plains.every((p) => p.length >= 40 && /[A-Za-z]/.test(p) && !/^-?\d+(?:\.\d+)?(?:\s*[a-z%°]+)?$/i.test(p))) {
    return null;
  }
  return cleaned
    .map((part, index) => {
      const repaired = repairEssayCorrectAnswerHtml(part);
      const plain = repaired.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      const alreadyLabeled = /^\s*(?:\d{1,2}\s*[).]|Answer\s+\d+\s*:|Option\s+\d+)/i.test(plain);
      const body = /<[a-z]/i.test(repaired) ? repaired : `<p>${repaired}</p>`;
      if (alreadyLabeled) return body;
      return `<p><strong>Option ${index + 1}</strong></p>${body}`;
    })
    .join("");
};

const renderTextCorrectAnswer = (answer, label = "Correct answer") => {
  let parsed = answer;
  if (typeof parsed === "string" && parsed.trim().startsWith("[")) {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      /* keep string */
    }
  }
  if (Array.isArray(parsed) && parsed.length > 1) {
    const essayHtml = formatEssayTopicPartsAsHtml(parsed);
    if (essayHtml) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, width: "100%" }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          <Box
            sx={{
              color: "success.dark",
              fontWeight: 600,
              fontSize: "0.875rem",
              width: "100%",
              "& p": { mb: 1.25 },
              "& p:last-child": { mb: 0 },
            }}
          >
            <HtmlWithLatex html={essayHtml} />
          </Box>
        </Box>
      );
    }
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {parsed.map((topic, i) => (
          <Box
            key={i}
            sx={{
              borderLeft: "3px solid",
              borderColor: "success.light",
              pl: 1.5,
              py: 0.5,
              bgcolor: "rgba(0,0,0,0.04)",
              borderRadius: "0 4px 4px 0",
            }}
          >
            <Typography
              component="div"
              variant="caption"
              sx={{ fontWeight: 700, mb: 0.5, display: "block", textTransform: "uppercase", letterSpacing: 0.4 }}
            >
              Option {i + 1}
            </Typography>
            <HtmlWithLatex html={repairEssayCorrectAnswerHtml(String(topic ?? ""))} />
          </Box>
        ))}
      </Box>
    );
  }
  const htmlAnswer = repairEssayCorrectAnswerHtml(String(answer ?? ""));
  const longProse = looksLikeLongEssayModelAnswer(htmlAnswer);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: longProse ? 0.75 : 0.5, width: "100%" }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Box
        sx={{
          color: "success.dark",
          fontWeight: longProse ? 600 : 700,
          fontSize: "0.875rem",
          width: "100%",
          ...(longProse ? { "& p": { mb: 1.25 }, "& p:last-child": { mb: 0 } } : {}),
        }}
      >
        <HtmlWithLatex html={stripAnswerBrackets(htmlAnswer)} />
      </Box>
    </Box>
  );
};

// A review row in the shape the shared-grid guides read. question_type is stated as "drawing" because
// `your_answer_interactive` on a review row is always the drawn half already — never the { blanks,
// interactive } wrapper a fill part stores while it is being answered.
const reviewGridPart = (r) => ({
  id: r.question_id,
  question_type: "drawing",
  answer_data: r.answer_data,
  image_url: r.image_url,
  image_urls: r.image_urls,
});

// ── Single question review card (used standalone and as a grouped sub-question) ─
const QuestionReviewCard = ({ row, label, aiFeedbackMap, aiFeedbackStatus, gridParts = null, gridAnswers = null, passageText = null }) => {
  const qt = String(row.question_type || "").toLowerCase().replace(/_/g, "-");
  const isText = ["short_answer", "essay", "short answer", "text"].some((t) => qt.includes(t));
  const isTableQ = qt === "table" && row.answer_data?.table_layout?.grid?.length > 0;
  const { questionNumber, subLabel } = parseReviewLabel(label);
  // The image-fill overlay below paints the question's figure with the answer boxes on it, so listing
  // that same figure as a static image printed it twice (CSSE 2015 Q17a). Only the overlay's OWN image
  // is dropped — a part that also carries a different figure to read still shows it.
  const overlayFigureKey = row.answer_data?.overlay_layout?.image_url
    ? String(row.answer_data.overlay_layout.image_url).split("?")[0].split("/").pop()
    : null;
  // Same for a drawing part: the canvases below paint the figure, and a part converted to a drawing
  // keeps its own crop of that same figure under another filename — printing both showed the axes
  // twice (Caterham 2016 Q20b). Only a lone image the canvas does not already carry is treated that way.
  const canvasFigureKey = row.answer_data?.interactive?.image_url || row.correct_interactive?.image_url
    ? String(row.answer_data?.interactive?.image_url || row.correct_interactive?.image_url)
      .split("?")[0].split("/").pop()
    : null;
  // One ownership rule: never paint the same diagram via HTML <img> and a separate figure strip.
  const reconciledFigures = reconcileRowFiguresForDisplay(dropPrintedDiagramFromSketchPart(row));
  const rowImagesCollected = reconciledFigures.images;
  const questionHtmlForReview = reconciledFigures.html;
  // The overlay editor saves a re-cropped COPY of the figure under its own filename, so no key test can
  // tell "this overlay IS the figure" (North London 2017 Q44b, which printed the pattern table flat and
  // again with the answer boxes on it) from "this overlay is a separate answer line and the figure still
  // has to be read" (St Paul's P3 Section C Q2f, whose worked example is the only thing that makes the
  // part answerable). The overlay carries the authored verdict; the static COUNT is not a signal — see
  // overlayReplacesFigure in StudentPaperTake.
  const rowImagesRaw = overlayFigureKey && row.answer_data?.overlay_layout?.replaces_figure === true
    ? []
    : rowImagesCollected.filter(
        (u) => !overlayFigureKey || String(u || "").split("?")[0].split("/").pop() !== overlayFigureKey
      );
  const figKey = (u) => String(u || "").split("?")[0].split("/").pop();
  const rowImages = canvasFigureKey
    ? (rowImagesRaw.length === 1 && !rowImagesRaw.some((u) => figKey(u) === canvasFigureKey)
        ? []
        : rowImagesRaw.filter((u) => figKey(u) !== canvasFigureKey))
    : rowImagesRaw;
  const tableSubmitted = isTableQ ? coerceFillAnswersArray(row.your_answer, parsePipeJoinedBlanks(row.correct_answer_label)) : [];
  const tableCorrect = isTableQ
    ? (
        row.answer_data?.correct_blanks?.map((b) => (Array.isArray(b) ? (b[0] ?? "") : String(b ?? "")))
        ?? parsePipeJoinedBlanks(row.correct_answer_label)
      )
    : [];
  const tableStemText = isTableQ
    ? stripEmbeddedTableHtml(
        subLabel ? extractSubCore(row.question_text || "", subLabel) : (row.question_text || "")
      )
    : "";
  const tableReviewQuestion = isTableQ
    ? {
        type: "table",
        paper_number: row.paper_number || undefined,
        question_text: tableStemText,
        answer_data: {
          ...(row.answer_data || {}),
          correct_blanks: row.answer_data?.correct_blanks
            ?? tableCorrect.map((v) => [v]),
        },
      }
    : null;

  const matchingRows = Array.isArray(row.matching_review) && row.matching_review.length > 0 ? row.matching_review : null;
  const interactiveSpec = row.answer_data?.interactive || null;
  const correctInteractive = row.correct_interactive || null;
  // An edge-drawn shape (reflect / rotate / closed polygon / "draw the parallelogram") submits the object
  // { points, edges } — not a bare array. The old Array.isArray() test rejected it, so the student's own
  // drawing silently rendered as "—" on review. Accept both shapes; keep the object whole so the canvas
  // can draw the outline from value.edges.
  const rawYourInteractive = row.your_answer_interactive;
  const interactiveGridSpec = interactiveSpec || correctInteractive;
  // Edges-only ("join the printed corners"), lines-with-none and cut-drag answers are objects with no
  // `points` array, so requiring one rendered them as "—". interactiveReviewValueOf keeps whatever
  // shape the student submitted (and wraps a reflect/rotate/polygon point list so review does not
  // auto-join corners).
  const yourInteractive = interactiveReviewValueOf(rawYourInteractive, interactiveGridSpec);
  const yourDeclaredNone = interactiveDeclaredNone(rawYourInteractive);
  const hasInteractive = !!(interactiveSpec || correctInteractive);
  // "Your shape" must show what the STUDENT drew. Part (c) of a shared-grid group ("mark D on the line
  // BC") lists C among its given_points, so this panel printed the answer's C inside the student's own
  // panel — and next to the mark they actually made in part (b), if it was somewhere else. Resolve the
  // points that belong to an earlier part against that part's stored answer; "Correct shape" below is
  // untouched, showing the model answer is its job.
  const yourGridSpec = (() => {
    // Only the row's OWN authored spec can be resolved: when this panel is falling back to
    // correctInteractive there is nothing of the student's to resolve it against.
    if (!interactiveSpec || !Array.isArray(gridParts) || gridParts.length < 2) return interactiveGridSpec;
    const self = reviewGridPart(row);
    const answers = gridAnswers || {};
    const given = sharedGridGivenPointsGuide(gridParts, answers, self);
    const hasGuideLabels = Array.isArray(interactiveSpec.original_shape_labels);
    const original = hasGuideLabels ? sharedGridOriginalShapeGuide(gridParts, answers, self) : interactiveSpec.original_shape;
    if (given === interactiveSpec.given_points && original === interactiveSpec.original_shape) return interactiveGridSpec;
    return { ...interactiveSpec, given_points: given, original_shape: original };
  })();
  // "Mark the fourth corner … then write its coordinates" — the drawing is only half the answer. The
  // typed half travels in row.your_answer, but the whole Your answer / Correct answer block below is
  // switched off for interactive rows, so the coordinate the student wrote was never shown after
  // submission (Reigate 2019 Q31c). Render it beside the shapes instead.
  const interactiveWrittenPrompt = correctInteractive?.written?.prompt || interactiveSpec?.written?.prompt || null;
  const interactiveWrittenCorrect = correctInteractive?.written?.answer || row.correct_answer_label || null;
  const showInteractiveWritten = hasInteractive
    && !!(interactiveWrittenPrompt || String(row.your_answer ?? "").trim() !== "");

  const overlay = row.answer_data?.overlay_layout;
  const isImageFillQ = qt === "image-fill"
    && overlay?.mode !== "inline"
    && Array.isArray(overlay?.blanks)
    && overlay.blanks.length > 0;
  const cleanBlank = (v) => {
    const raw = Array.isArray(v) ? (v[0] ?? "") : (v ?? "");
    return decodeHtmlEntities(String(raw).replace(/&nbsp;/gi, " ").replace(/<[^>]*>/g, "").trim());
  };
  const imageFillCorrect = isImageFillQ
    ? overlay.blanks.map((_, i) => cleanBlank((row.answer_data?.correct_blanks || [])[i]))
    : [];
  const imageFillSubmitted = isImageFillQ
    ? coerceFillAnswersArray(row.your_answer, imageFillCorrect).map((v) => cleanBlank(v))
    : [];
  // A question with more than one right answer (St Albans 2019 Q20b: two valid routes) showed only
  // the first, so a student who wrote the other one saw a "different" answer as the key.
  const imageFillOtherAnswers = isImageFillQ && Array.isArray(row.answer_data?.answer_variants)
    ? [...new Set(row.answer_data.answer_variants
        .map((variant) => (Array.isArray(variant) ? variant : [variant]).map((v) => cleanBlank(v)).join(" | "))
        .filter((joined) => joined.replace(/[\s|]/g, "") !== ""))]
        // Notation-only variants (n×(n-1), n*(n-1), nx(n-1)) are the same answer — list real alternatives.
        .filter((joined, _, all) => {
          const bare = (v) => String(v).toLowerCase().replace(/[\s×*·x]/g, "");
          return bare(joined) !== bare(imageFillCorrect.join(" | "))
            && all.findIndex((other) => bare(other) === bare(joined)) === all.indexOf(joined);
        })
    : [];
  const imageFillBlankMatches = isImageFillQ
    ? overlay.blanks.map((_, i) => {
        const raw = (row.answer_data?.correct_blanks || [])[i];
        // answer_variants are whole alternative answers ("12×11" or "11×12"); box i of each counts too.
        const variantAlts = (Array.isArray(row.answer_data?.answer_variants) ? row.answer_data.answer_variants : [])
          .map((variant) => (Array.isArray(variant) ? variant[i] : i === 0 ? variant : null));
        const alts = [...(Array.isArray(raw) ? raw : [raw]), ...variantAlts]
          .map((value) => cleanBlank(value))
          .filter(Boolean);
        if (alts.length === 0) return null;
        const yours = imageFillSubmitted[i] ?? "";
        return alts.some((alt) => singleValueMatches(yours, alt));
      })
    : null;

  // Multi-blank fills and tables are graded positionally by the backend (blank i ↔ correct[i]).
  // The client-side isAnswerCorrect/isAnswerPartial do order-independent matching, which would
  // wrongly accept swapped / wrong-cell values — trust the backend verdict for these.
  const blankCount = row.answer_data?.correct_blanks?.length
    || row.answer_data?.blank_labels?.length
    || parsePipeJoinedBlanks(row.correct_answer_label).length;
  // image-fill is a multi-blank fill too — isFillQuestionType leaves it out because the display code
  // renders it through its own overlay path, not because the server grades it any differently.
  const isMultiBlankFill = (isFillQuestionType(qt) || qt === "image-fill") && blankCount > 1;
  // A multi-blank fill's boxes mean nothing as one comma-joined run. St Paul's P2 Q14b asks which
  // coins make 24p, 38p, 57p, £1.18 and £1.26 and review printed
  // "10p, 10p, 2p, 2p, , 50p, 5p, 1p, 1p, , £1, 20p, 5p, 1p" — every box's value with nothing saying
  // which amount it answered, and the two amounts that cannot be made as bare gaps. The blank labels
  // and the positional correct_blanks are already on answer_data, so pair each box with its own
  // label, in the order the server graded them.
  const blankLabelList = Array.isArray(row.answer_data?.blank_labels)
    ? row.answer_data.blank_labels.map((l) => cleanBlank(l).replace(/[-–—:]+\s*$/, "").trim())
    : [];
  const labelledBlankRows = (() => {
    if (isTableQ || isImageFillQ || hasInteractive || matchingRows) return null;
    if (!isMultiBlankFill) return null;
    // Nothing to add when the boxes were never labelled — the flat list below reads the same.
    if (!blankLabelList.some((l) => l !== "")) return null;
    const correct = Array.isArray(row.answer_data?.correct_blanks)
      ? row.answer_data.correct_blanks.map((b) => cleanBlank(b))
      : parsePipeJoinedBlanks(row.correct_answer_label).map((b) => cleanBlank(b));
    const count = Math.max(blankLabelList.length, correct.length);
    if (count < 2) return null;
    const submitted = coerceFillAnswersArray(row.your_answer, correct).map((v) => cleanBlank(v));
    // Interchangeable boxes ("Write down two calculations with the same answer") are graded as a set,
    // so box i is right when it matches ANY unused scheme value. Comparing positionally painted a
    // swapped, fully-credited pair red beside a "Correct" chip (St Paul's Sample P2 2025 B Q3). The
    // correct column is reordered to sit each matched value next to the box that gave it.
    // "8×7=56" for "7×8=56" is the same calculation — the server accepts it, so the colour must too.
    const boxMatches = (your, want) => singleValueMatches(your, want) || commutedCalculationMatches(your, want);
    let wantList = correct;
    // Several whole answers can be right ("two numbers with a sum of -3": -5/2, -4/1, -3/0, -2/-1). The
    // server graded against answer_variants, so pair the boxes with the variant the student actually
    // gave — comparing to the one stored key painted a correct "-1, -2" red (Perse Specimen 3 Q9b).
    const variants = (Array.isArray(row.answer_data?.answer_variants) ? row.answer_data.answer_variants : [])
      .filter(Array.isArray)
      .map((v) => v.map((x) => cleanBlank(x)));
    const pooled = fillBlanksAreInterchangeable(row);
    const variantMatchesBoxes = (v) => {
      if (v.length !== count) return false;
      const used = new Set();
      return Array.from({ length: count }, (_, i) => submitted[i] ?? "").every((your, i) => {
        if (your === "") return false;
        if (!pooled) return boxMatches(your, v[i]);
        const j = v.findIndex((c, k) => !used.has(k) && c !== "" && boxMatches(your, c));
        if (j === -1) return false;
        used.add(j);
        return true;
      });
    };
    const givenVariant = variants.find(variantMatchesBoxes);
    if (givenVariant) wantList = givenVariant;
    if (pooled) {
      const used = new Set();
      const paired = Array.from({ length: count }, (_, i) => {
        const your = submitted[i] ?? "";
        if (your === "") return null;
        const j = wantList.findIndex((c, k) => !used.has(k) && c !== "" && boxMatches(your, c));
        if (j === -1) return null;
        used.add(j);
        return wantList[j];
      });
      const rest = wantList.filter((_, k) => !used.has(k));
      wantList = paired.map((p) => p ?? rest.shift() ?? "");
    }
    return Array.from({ length: count }, (_, i) => {
      const your = submitted[i] ?? "";
      const want = wantList[i] ?? "";
      return {
        label: blankLabelList[i] ?? "",
        your,
        correct: want,
        // Per-box colouring only where both sides carry a value — a box whose scheme is empty
        // ("38p cannot be made with four coins") has nothing to be right or wrong against.
        matched: your !== "" && want !== "" ? boxMatches(your, want) : null,
      };
    });
  })();

  // The other whole answers the server would also have accepted, shown under the boxes so a student
  // can see the question had more than one right answer.
  const otherAcceptedAnswers = (() => {
    if (!labelledBlankRows || !Array.isArray(row.answer_data?.answer_variants)) return [];
    const key = (list) => list.join(" | ");
    const shown = key(labelledBlankRows.map((b) => b.correct));
    const primary = Array.isArray(row.answer_data?.correct_blanks) ? row.answer_data.correct_blanks.map((b) => cleanBlank(b)) : [];
    const all = [primary, ...row.answer_data.answer_variants.filter(Array.isArray).map((v) => v.map((x) => cleanBlank(x)))]
      .filter((v) => v.length > 0 && v.every((x) => x !== ""));
    const seen = new Set([shown]);
    return all.filter((v) => {
      const k = key(v);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    }).map((v) => v.join(", "));
  })();

  // "Answer must be written exactly like this": the server compared it literally, so neither the
  // client matcher nor the AI panel below may reopen the verdict.
  const exactOnly = !!(row.exact_match || row.answer_data?.exact_match);
  // An authored answer rule graded this row against its whole accepting set, and
  // `correct_answer_label` is the rule's prose description of that set rather than a value to compare
  // against. Every matcher below — and the stored AI sentence, which only ever saw the one example
  // mark scheme — reads it as a single answer and calls a right answer wrong.
  const ruleGraded = !!(row.answer_rule || row.answer_data?.answer_rule);
  // Server verdicts are authoritative — the client matcher only fills in when the server sent
  // none (is_correct == null), so an explicit backend "incorrect" is never upgraded here.
  const ruleIsCorrect = row.is_correct == null
    ? (!exactOnly && !isText && !isMultiBlankFill && isAnswerCorrect(row.your_answer, row.correct_answer_label, { questionText: row.question_text }))
    : !!row.is_correct;
  // Same value-list credit resolveLeafVerdict awards, so the chip, the "Full answer" heading and the AI
  // panel below say what the Marks Breakdown scored.
  // A tick-all-that-apply row's `your_answer` is the ticked labels joined with ", " against a mark
  // scheme listing every correct one — the same single-value trap multi-blank fills fall into, so the
  // server's set comparison is the only verdict, here as in resolveLeafVerdict.
  const isMultiSelectRow = String(row.question_type || "").toLowerCase().replace(/_/g, "-") === "multiple-select"
    || (Array.isArray(row.options) && row.options.filter((o) => o?.is_correct === true).length >= 2);
  // A drawing row's `your_answer` is only the typed half of "mark D AND write its coordinates" — see
  // resolveLeafVerdict, which excludes them for the same reason. Scoring that half as a value list here
  // would caption the card with a count ("1 of 2 correct") the breakdown never awarded.
  const isDrawingRow = String(row.question_type || "").toLowerCase().replace(/_/g, "-") === "drawing";
  const listPartial = (!isMultiBlankFill && !isMultiSelectRow && !isDrawingRow && !exactOnly && !ruleGraded && !ruleIsCorrect)
    ? (arrowSequencePositionalCredit(row.your_answer, row.correct_answer_label)
      || numericListPartialCredit(row.your_answer, row.correct_answer_label))
    : null;
  const ruleIsPartial = !ruleIsCorrect && (!!row.is_partial || !!listPartial || (row.is_correct == null && !exactOnly && !isText && !isMultiBlankFill && isAnswerPartial(row.your_answer, row.correct_answer_label)));

  // A multi-blank fill was graded box by box by the server, so the AI's whole-answer read of
  // "5 | 1000" against "5, 1000" is not a verdict on it — showing that sentence under a row the
  // breakdown scored full marks is the "correct answer marked incorrect in AI evaluation" report.
  const aiOptedOut = exactOnly || ruleGraded || isMultiBlankFill;
  const aiq = aiOptedOut ? null : aiFeedbackMap?.[String(row.question_id)];
  const aiSaysCorrect = aiq?.is_correct;
  const aiIsPartial = aiq?.partial_score != null && aiq.partial_score > 0 && aiq.partial_score < 1;
  // Excluded for multi-blank fills for the same reason resolveLeafVerdict excludes them: `your_answer`
  // is the boxes joined with "|" against a display string listing every box's value, so a whole-answer
  // guard judges the two lists as one value and forces "Incorrect" over the server's per-box verdict.
  // An explicit server Correct is final — this guard reads a whole answer as one string and cannot see
  // the answer_variants / rule / pooled blanks the server graded against. Mirrors resolveLeafVerdict.
  const detWrong = row.is_correct !== true
    && !isMultiBlankFill && !isMultiSelectRow && !ruleGraded && !!(row.your_answer && row.correct_answer_label
    && (submissionIsDeterministicallyWrong(row.your_answer, row.correct_answer_label, row.question_text)
      || submittedMissesPercentSign(row.your_answer, row.correct_answer_label, row)));
  // Same resolver the Marks Breakdown tab and the Result page use, so a card can never show "Correct"
  // over a row the breakdown scored 0.
  const verdict = resolveLeafVerdict(row, { aiFeedbackMap, aiFeedbackStatus });
  const effectivelyCorrect = verdict.result === "correct";
  const effectivelyPartial = verdict.result === "partial";

  // For a chosen option, show the option they clicked. The server resolves your_answer from the
  // stored key and, when that key couldn't be matched, used to fall through to a guessed index —
  // which is how the mark scheme ended up printed back as the student's own answer.
  const pickedOption = Number.isInteger(row.selected_index)
    ? row.options?.[row.selected_index]
    : null;
  const yourAnswerHtml = pickedOption
    ? `${["A", "B", "C", "D", "E"][row.selected_index] ?? row.selected_index + 1}: ${pickedOption.option_text ?? ""}`
    : row.your_answer;

  // A matching answer that is already printed in the question is not a blank submission.
  const embeddedCorrectLabel = reviewYourAnswerWasCorrectLabel({
    questionText: row.question_text,
    passageText,
    correctAnswer: row.correct_answer_label || row.correct_answer,
    studentAnswer: row.your_answer,
    isCorrect: row.is_correct === true || effectivelyCorrect,
    isPartial: effectivelyPartial || row.is_partial === true,
    hasDrawing: !!row.your_answer_drawing,
    hasInteractive: !!row.your_answer_interactive,
  });

  // A blank submission is "not answered" — it must not be shown as Incorrect.
  const hasAnswer = !!embeddedCorrectLabel
    || (row.your_answer != null && String(row.your_answer).trim() !== "")
    || !!row.your_answer_drawing
    || !!row.your_answer_interactive;

  const borderColor = embeddedCorrectLabel
    ? "#4caf50"
    : !hasAnswer
    ? "#9e9e9e"
    : (isText && !exactOnly && !ruleGraded && !effectivelyPartial)
    // The server's explicit Incorrect stands over a stored AI "Correct", as the chip already shows.
    ? ((detWrong || (row.is_correct === false && !effectivelyCorrect))
        ? "#ef5350"
        : (aiq && aiFeedbackStatus === "completed"
          ? (aiSaysCorrect ? "#4caf50" : aiIsPartial ? "#f59e0b" : "#ef5350")
          : "#9e9e9e"))
    : effectivelyCorrect ? "#4caf50"
    : effectivelyPartial ? "#f59e0b"
    : "#ef5350";

  return (
    <Card
      id={`question-${row.question_id}`}
      sx={{ mb: 0, borderRadius: 2, borderLeft: "4px solid", borderLeftColor: borderColor }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
          {!isTableQ && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{label}</Typography>
              <Chip size="small" label={`${fmtMarks(row.marks)} marks`} variant="outlined" />
            </>
          )}
          {embeddedCorrectLabel ? (
            <Chip icon={<CheckCircleIcon />} label="Correct" color="success" size="small" sx={{ fontWeight: 700 }} />
          ) : !hasAnswer ? (
            <Chip icon={<HelpOutlineIcon />} label="Not answered" color="default" variant="outlined" size="small" sx={{ fontWeight: 600 }} />
          ) : (isText && !exactOnly && !ruleGraded && !effectivelyPartial && !effectivelyCorrect) ? (
            (detWrong || (row.is_correct === false && !effectivelyCorrect)) ? (
              <Chip icon={<CancelIcon />} label="Incorrect" color="error" size="small" sx={{ fontWeight: 700 }} />
            ) : aiq && aiFeedbackStatus === "completed" ? (
              aiSaysCorrect && effectivelyCorrect
                ? <Chip icon={<CheckCircleIcon />} label="AI Correct" color="success" size="small" sx={{ fontWeight: 700 }} />
                : aiIsPartial
                  ? <Chip label="Partially Correct" size="small" sx={{ fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }} />
                  : <Chip icon={<CancelIcon />} label="AI Incorrect" color="error" size="small" sx={{ fontWeight: 700 }} />
            ) : (
              <Chip icon={<HelpOutlineIcon />} label="Written / manual" color="default" size="small" sx={{ fontWeight: 600 }} />
            )
          ) : effectivelyCorrect ? (
            <Chip icon={<CheckCircleIcon />} label="Correct" color="success" size="small" sx={{ fontWeight: 700 }} />
          ) : effectivelyPartial ? (
            <Chip
              label={listPartial
                ? `Partially correct — ${listPartial.matched} of ${listPartial.total} correct${listPartial.extra ? `, ${listPartial.extra} not in the answer` : ""}`
                : "Partially correct"}
              size="small"
              sx={{ fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }} />
          ) : (
            <Chip icon={<CancelIcon />} label="Incorrect" color="error" size="small" sx={{ fontWeight: 700 }} />
          )}
        </Box>

        {row.scan_note?.message ? (
          <Alert
            severity={row.scan_note.kind === "skipped" ? "info" : "warning"}
            sx={{ mb: 1.5, borderRadius: 2, py: 0.25 }}
          >
            <Typography variant="body2">{row.scan_note.message}</Typography>
          </Alert>
        ) : null}

        {rowImages.map((url, ui) => (
          <Box key={ui} sx={{ mb: 1 }}>
            <Box component="img" src={url} alt=""
              sx={{ maxWidth: "100%", maxHeight: 240, borderRadius: 1, border: "1px solid", borderColor: "divider" }} />
          </Box>
        ))}

        {!isTableQ && (
          <Box sx={{ mb: 1.5 }}>
            <HtmlWithLatex html={questionHtmlForReview || ""} />
          </Box>
        )}

        {isTableQ && tableReviewQuestion ? (
          <Box sx={{ mb: 1.5 }}>
            <QuestionPaperLayout
              question={tableReviewQuestion}
              questionNumber={questionNumber ?? (row.paper_number ? Number(row.paper_number) : undefined)}
              parentNumber={questionNumber ?? (row.paper_number ? Number(row.paper_number) : undefined)}
              subLabel={subLabel}
              showAnswers
              studentAnswers={tableSubmitted}
              hideImage={rowImages.length > 0}
            />
          </Box>
        ) : null}

        {isImageFillQ ? (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 240 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Your answer
              </Typography>
              {imageFillSubmitted.some((v) => v !== "") ? (
                <ImageOverlayFillInput
                  overlayLayout={overlay}
                  values={imageFillSubmitted}
                  revealValues={imageFillSubmitted}
                  // A box can't be wrong on an answer the server marked correct: this client re-check
                  // misses pooled answers and rules (Sevenoaks Y7 2025 Q19a drew "11×12" red).
                  blankMatches={effectivelyCorrect && imageFillBlankMatches
                    ? imageFillBlankMatches.map((m) => (m === false ? true : m))
                    : imageFillBlankMatches}
                  prefilled={row.answer_data?.blank_labels}
                  readOnly
                />
              ) : (
                <Typography variant="body1" color="text.disabled">—</Typography>
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 240 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                {ruleIsPartial || !effectivelyCorrect ? "Full answer" : "Correct answer"}
              </Typography>
              <ImageOverlayFillInput
                overlayLayout={overlay}
                values={imageFillCorrect}
                revealValues={imageFillCorrect}
                prefilled={row.answer_data?.blank_labels}
                readOnly
              />
              {imageFillOtherAnswers.length > 0 && (
                <Typography variant="body2" sx={{ mt: 0.75, color: "success.dark" }}>
                  <strong>Also accepted:</strong> {imageFillOtherAnswers.join(" or ")}
                </Typography>
              )}
            </Box>
          </Box>
        ) : null}

        {!isText && !matchingRows && row.options?.length >= 2 && row.options.some((opt) => opt.is_correct || (!!opt.option_text && splitMultiValueAnswer(row.correct_answer_label ?? "").some((c) => singleValueMatches(opt.option_text, c)))) && (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 1, mb: 1.5 }}>
            {row.options.map((opt, oi) => {
              const letter = ["A", "B", "C", "D", "E"][oi] ?? String(oi + 1);
              const correctParts = splitMultiValueAnswer(row.correct_answer_label ?? "");
              const isCorrectOpt = opt.is_correct === true
                || (!!opt.option_text && correctParts.some((c) => singleValueMatches(opt.option_text, c)));
              const yourParts = splitMultiValueAnswer(row.your_answer ?? "");
              // Prefer the index the student actually clicked. Value-matching option text was
              // lighting up the wrong option whenever two options are equal in value ("1/4" vs
              // "0.25"), or whenever every option is labelled the same way ("Option A".."Option D")
              // — that is how picking A came back showing Option C as the student's own answer.
              // Multi-select rows have no single index, so they keep the value match.
              const isSelectedOpt = Number.isInteger(row.selected_index)
                ? row.selected_index === oi
                : !!opt.option_text && (
                  yourParts.some((y) => singleValueMatches(opt.option_text, y))
                  || yourParts.includes(String(oi))
                );
              return (
                <Box
                  key={opt.id ?? oi}
                  sx={{
                    p: 1, borderRadius: 1, border: "1px solid",
                    borderColor: isCorrectOpt ? "success.light" : isSelectedOpt ? "error.light" : "divider",
                    bgcolor: isCorrectOpt ? "#f0fdf4" : isSelectedOpt ? "#fff5f5" : "#fafafa",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block" }}>
                    {letter}:
                  </Typography>
                  {opt.image_url && (
                    <Box sx={{ mb: 0.5 }}>
                      <img
                        src={opt.image_url}
                        alt={opt.option_text || `Option ${oi + 1}`}
                        style={{ maxWidth: "100%", objectFit: "contain", borderRadius: 4 }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </Box>
                  )}
                  <Box sx={{ fontSize: "0.8rem", mb: 0.25 }}>
                    <HtmlWithLatex html={opt.option_text ?? ""} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: isCorrectOpt ? "success.main" : isSelectedOpt ? "error.main" : "text.disabled",
                    }}
                  >
                    {isCorrectOpt ? "Correct" : "Incorrect"}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        {matchingRows ? (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.75 }}>
              Your links
            </Typography>
            <MatchingReview rows={matchingRows} />
          </Box>
        ) : null}

        {hasInteractive ? (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 240 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Your shape
              </Typography>
              {yourInteractive && interactiveGridSpec ? (
                <>
                  <InteractiveDiagramCanvas
                    spec={yourGridSpec}
                    imageUrl={yourGridSpec.image_url || row.image_url || ""}
                    readOnly
                    value={yourInteractive}
                  />
                  {yourDeclaredNone && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      Answered: nothing to draw
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body1" color="text.disabled">—</Typography>
              )}
            </Box>
            {correctInteractive ? (
              <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 240 } }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Correct shape
                </Typography>
                {(correctInteractive.prefer_solution_image || correctInteractive.reveal_image_url)
                  && (correctInteractive.reveal_image_url || row.correct_drawing) ? (
                  <Box component="img"
                    src={correctInteractive.reveal_image_url || row.correct_drawing}
                    alt="Correct solution"
                    sx={{ maxWidth: "100%", maxHeight: 320, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white" }} />
                ) : (
                  <InteractiveDiagramCanvas
                    spec={correctInteractive}
                    imageUrl={correctInteractive.image_url || row.image_url || ""}
                    readOnly
                    revealValue={interactiveRevealValue(correctInteractive)}
                  />
                )}
              </Box>
            ) : null}
            {row.your_answer_drawing && (
              <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 240 } }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Your working (drawing)
                </Typography>
                <Box component="img" src={row.your_answer_drawing} alt="Submitted working"
                  sx={{ maxWidth: "100%", maxHeight: 320, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white" }} />
              </Box>
            )}
            {row.correct_drawing && (
              <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 240 } }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Correct plotted solution
                </Typography>
                <Box component="img" src={row.correct_drawing} alt="Correct plotted solution"
                  sx={{ maxWidth: "100%", maxHeight: 320, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white" }} />
              </Box>
            )}
          </Box>
        ) : null}

        {showInteractiveWritten ? (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                {interactiveWrittenPrompt || "Your answer"}
              </Typography>
              {String(row.your_answer ?? "").trim() !== "" ? (
                <Box sx={{
                  color: effectivelyCorrect ? "success.main" : effectivelyPartial ? "#b45309" : "error.main",
                  fontWeight: effectivelyCorrect ? 700 : 400,
                  fontSize: "0.875rem",
                }}>
                  <HtmlWithLatex html={stripAnswerBrackets(String(row.your_answer))} />
                </Box>
              ) : (
                <Typography variant="body1" color="text.disabled">—</Typography>
              )}
            </Box>
            {interactiveWrittenCorrect ? (
              <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 160 }, width: { xs: "100%", sm: "auto" } }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Correct answer
                </Typography>
                <Box sx={{ color: "success.dark", fontWeight: 700, fontSize: "0.875rem" }}>
                  <HtmlWithLatex html={stripAnswerBrackets(String(interactiveWrittenCorrect))} />
                </Box>
              </Box>
            ) : null}
          </Box>
        ) : null}

        {!isTableQ && !isImageFillQ && !hasInteractive && !matchingRows ? (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: labelledBlankRows ? "1 1 100%" : 1, minWidth: 160 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
              Your answer
              {ruleIsPartial && (
                <Typography component="span" variant="body2" sx={{ ml: 0.75, color: "#b45309", fontWeight: 700 }}>
                  — Partially correct
                </Typography>
              )}
            </Typography>
            {labelledBlankRows ? (
              <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(60px, max-content) 1fr 1fr",
                  columnGap: 2,
                  rowGap: 0.75,
                  alignItems: "start",
                  fontSize: "0.875rem",
                }}
              >
                <Box />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Your answer
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  {ruleIsPartial ? "Full answer" : "Correct answer"}
                </Typography>
                {labelledBlankRows.map((b, i) => (
                  <Fragment key={i}>
                    <Box sx={{ fontWeight: 600, color: "text.secondary" }}>
                      {b.label ? <HtmlWithLatex html={b.label} /> : `Box ${i + 1}`}
                    </Box>
                    <Box
                      sx={{
                        // The row's own Correct verdict wins: the server may have accepted a box this
                        // client matcher can't (a rule, a pooled pair), and red under "Correct" is a lie.
                        color: b.matched === null && !effectivelyCorrect ? "text.primary" : (b.matched || effectivelyCorrect) ? "success.main" : "error.main",
                        fontWeight: (b.matched || effectivelyCorrect) ? 700 : 400,
                      }}
                    >
                      {b.your ? <HtmlWithLatex html={b.your} /> : <Box component="span" sx={{ color: "text.disabled" }}>—</Box>}
                    </Box>
                    <Box sx={{ color: "success.dark", fontWeight: 700 }}>
                      {b.correct ? <HtmlWithLatex html={b.correct} /> : <Box component="span" sx={{ color: "text.disabled" }}>—</Box>}
                    </Box>
                  </Fragment>
                ))}
              </Box>
              {otherAcceptedAnswers.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                  Also accepted: {otherAcceptedAnswers.join("  ·  ")}
                </Typography>
              )}
              </>
            ) : yourAnswerHtml ? (
              <Box
                sx={{
                  color: isText ? "text.primary"
                    : effectivelyCorrect ? "success.main"
                    : effectivelyPartial ? "#b45309"
                    : "error.main",
                  fontWeight: effectivelyCorrect ? 700 : 400,
                  fontSize: "0.875rem",
                }}
              >
                <HtmlWithLatex html={stripAnswerBrackets(yourAnswerHtml)} />
              </Box>
            ) : !row.your_answer_drawing ? (
              <Typography variant="body1" color="text.disabled">—</Typography>
            ) : null}
            {row.your_answer_drawing && (
              <Box sx={{ mt: row.your_answer ? 1 : 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                  Your working (drawing)
                </Typography>
                <Box component="img" src={row.your_answer_drawing} alt="Submitted working"
                  sx={{ maxWidth: "100%", maxHeight: 320, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white" }} />
              </Box>
            )}
          </Box>

          {!isText && !labelledBlankRows && row.correct_answer_label && (
            <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 160 }, width: { xs: "100%", sm: "auto" } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                {ruleIsPartial ? "Full answer" : "Correct answer"}
              </Typography>
              <Box sx={{ color: "success.dark", fontWeight: 700, fontSize: "0.875rem" }}>
                <HtmlWithLatex html={stripAnswerBrackets(
                  completeTruncatedMixedCorrectAnswer(row.correct_answer_label, row.answer_explanation)
                  || statedAreaWhenQuestionAsksForArea(row.question_text, row.answer_explanation, row.correct_answer_label)
                  || row.correct_answer_label
                )} />
              </Box>
            </Box>
          )}
          {isText && !labelledBlankRows && (row.correct_answer_label || row.correct_answer) ? (
            <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 220 }, width: { xs: "100%", sm: "auto" } }}>
              {renderTextCorrectAnswer(
                completeTruncatedMixedCorrectAnswer(row.correct_answer_label || row.correct_answer, row.answer_explanation)
                || statedAreaWhenQuestionAsksForArea(row.question_text, row.answer_explanation, row.correct_answer_label || row.correct_answer)
                || row.correct_answer_label || row.correct_answer,
                ruleIsPartial ? "Full answer" : "Correct answer"
              )}
            </Box>
          ) : null}
          {row.correct_drawing && (
            <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 220 }, width: { xs: "100%", sm: "auto" } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Correct plotted solution
              </Typography>
              <Box component="img" src={row.correct_drawing} alt="Correct plotted solution"
                sx={{ maxWidth: "100%", maxHeight: 320, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white" }} />
            </Box>
          )}
        </Box>
        ) : null}

        <AiFeedbackPanel
          questionId={row.question_id}
          aiFeedbackMap={aiOptedOut ? null : aiFeedbackMap}
          aiFeedbackStatus={aiFeedbackStatus}
          questionMarks={row.marks}
          ruleIsCorrect={effectivelyCorrect || ruleIsCorrect}
          ruleIsPartial={effectivelyPartial || ruleIsPartial}
          forceIncorrect={(detWrong || row.is_correct === false) && !effectivelyPartial && !effectivelyCorrect}
        />

        {row.answer_explanation && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#f8faff", borderRadius: 2, border: "1px solid #e8ecff" }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#1e40af", display: "block", mb: 0.5 }}>
              Explanation
            </Typography>
            <Box sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}>
              <HtmlWithLatex html={row.answer_explanation} />
            </Box>
          </Box>
        )}

        {row.explanation_video_url && row.video_generation_status === "completed" && (
          <Box sx={{ mt: 2 }}>
            <VideoExplanationPlayer
              videoUrl={row.explanation_video_url}
              questionText={row.question_text || label}
            />
          </Box>
        )}
        {row.video_generation_status && row.video_generation_status !== "completed" && !row.explanation_video_url && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Explanation video: {row.video_generation_status.replace(/_/g, " ")}…
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ── Grouped question: stem + nested sub-question review cards ──────────────────
const GroupedReviewCard = ({ row, number, aiFeedbackMap, aiFeedbackStatus }) => {
  const subs = row.sub_questions || [];
  const stemText = row.question_text || row.group_title || "";
  // The group's parts and the drawing each one submitted, so a part can resolve the points it inherits
  // from an earlier part to that student's own mark instead of the authored answer.
  const gridParts = subs.map(reviewGridPart);
  const gridAnswers = Object.fromEntries(subs.map((s) => [s.question_id, s.your_answer_interactive]));
  const reconciledStem = reconcileRowFiguresForDisplay({
    ...row,
    question_text: stemText,
  });
  const groupHasVideo = Boolean(row.explanation_video_url) && row.video_generation_status === "completed";

  return (
    <Card sx={{ mb: 2, borderRadius: 2, borderLeft: "4px solid", borderLeftColor: "#7c3aed" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Q{number}</Typography>
          <Chip size="small" label={`${subs.length} part${subs.length === 1 ? "" : "s"}`} variant="outlined" />
          {row.section_title && <Chip size="small" label={row.section_title} variant="outlined" />}
        </Box>

        {reconciledStem.images.map((url, ui) => (
          <Box key={ui} sx={{ mb: 1 }}>
            <Box component="img" src={url} alt=""
              sx={{ maxWidth: "100%", maxHeight: 260, borderRadius: 1, border: "1px solid", borderColor: "divider" }} />
          </Box>
        ))}

        {reconciledStem.html && (
          <Box sx={{ mb: 1.5 }}>
            <HtmlWithLatex html={reconciledStem.html} />
          </Box>
        )}

        {row.section_instructions && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: "italic" }}>
            {row.section_instructions}
          </Typography>
        )}

        {/* One video explains the main question and every part, one after another. */}
        {groupHasVideo && (
          <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <VideoExplanationPlayer
              videoUrl={row.explanation_video_url}
              questionText={stemText.replace(/<[^>]*>/g, "").trim() || `Q${number}`}
            />
            <Typography variant="caption" color="text.secondary">
              Explains all {subs.length} parts
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {subs.map((sub, i) => (
            <QuestionReviewCard
              key={sub.question_id ?? i}
              // With the whole-question video, a part's own older video would only repeat it.
              row={groupHasVideo ? { ...sub, explanation_video_url: null, video_generation_status: null } : sub}
              label={`Q${number}${SUB_LETTERS[i] ?? i + 1}`}
              aiFeedbackMap={aiFeedbackMap}
              aiFeedbackStatus={aiFeedbackStatus}
              gridParts={gridParts}
              gridAnswers={gridAnswers}
              passageText={stemText}
            />
          ))}
        </Box>

        {row.answer_explanation && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#f8faff", borderRadius: 2, border: "1px solid #e8ecff" }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#1e40af", display: "block", mb: 0.5 }}>
              Explanation
            </Typography>
            <Box sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}>
              <HtmlWithLatex html={row.answer_explanation} />
            </Box>
          </Box>
        )}

      </CardContent>
    </Card>
  );
};

const StudentPaperReview = () => {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const [tab, setTab] = useState("questions");
  const [aiFeedbackStatus, setAiFeedbackStatus] = useState("pending");
  const [aiFeedbackMap, setAiFeedbackMap] = useState({});
  const [aiOverall, setAiOverall] = useState(null);
  const [aiScore, setAiScore] = useState(null);
  const [aiPercentage, setAiPercentage] = useState(null);
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  const MAX_POLLS = 20;

  useEffect(() => {
    const handleExamModeChanged = () => window.location.reload();
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  const load = useCallback(async () => {
    if (!purchaseId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/${getPastPaperApiPrefix()}/paper/${purchaseId}/submission-review`);
      const data = res.data?.data ?? res.data;
      if (!data?.questions) {
        setPayload(null);
        setError("No review data returned.");
      } else {
        setPayload(data);
        if (data.ai_feedback_status) setAiFeedbackStatus(data.ai_feedback_status);
        if (data.ai_score != null) setAiScore(data.ai_score);
      }
    } catch (e) {
      const msg = e.response?.data?.message || "Could not load review.";
      setError(msg);
      toast.error(msg);
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [purchaseId]);

  const fetchAiFeedback = useCallback(async () => {
    if (!purchaseId) return;
    try {
      const res = await api.get(`/${getPastPaperApiPrefix()}/paper/${purchaseId}/ai-feedback`);
      const p = res.data?.data ?? res.data;
      const status = p?.status ?? "pending";
      setAiFeedbackStatus(status);
      if (status === "completed") {
        setAiFeedbackMap(p?.question_feedback ?? {});
        setAiOverall(p?.overall ?? null);
        setAiScore(p?.ai_score ?? null);
        setAiPercentage(p?.ai_percentage ?? null);
        clearInterval(pollRef.current);
        try {
          const r2 = await api.get(`/${getPastPaperApiPrefix()}/paper/${purchaseId}/submission-review`);
          const d = r2.data?.data ?? r2.data;
          setPayload(d);
        } catch {
          // non-critical
        }
      } else if (status === "failed") {
        clearInterval(pollRef.current);
      }
    } catch {
      // silently ignore polling errors
    }
  }, [purchaseId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!purchaseId) return;
    fetchAiFeedback();
    pollRef.current = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current >= MAX_POLLS) { clearInterval(pollRef.current); return; }
      if (aiFeedbackStatus === "completed" || aiFeedbackStatus === "failed") { clearInterval(pollRef.current); return; }
      fetchAiFeedback();
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [purchaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (aiFeedbackStatus === "completed" || aiFeedbackStatus === "failed") {
      clearInterval(pollRef.current);
    }
  }, [aiFeedbackStatus]);

  useEffect(() => {
    if (!loading && payload && hash) {
      const id = hash.replace(/^#/, "");
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [loading, payload, hash]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error && !payload) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} variant="contained" onClick={() => navigate(getPastPaperPortalHomePath())}>
          Back to Practice Papers
        </Button>
      </Box>
    );
  }

  const questions = payload?.questions || [];
  // The AI score refines the deterministic score, it never lowers it — an AI score of 0 is `!= null`, so
  // taking it verbatim let a failed/under-reporting AI wipe marks the rule grader had confirmed.
  const ruleScoreNum = Number(payload?.score ?? 0);

  const computeLeafMarks = (row) => resolveLeafVerdict(row, { aiFeedbackMap, aiFeedbackStatus });

  const breakdownRows = [];
  questions.forEach((row, idx) => {
    const isGrouped = String(row.type || row.question_type || "").toLowerCase() === "grouped"
      || (Array.isArray(row.sub_questions) && row.sub_questions.length > 0);
    const mainNum = idx + 1;
    if (isGrouped) {
      (row.sub_questions || []).forEach((sub, si) => {
        const m = computeLeafMarks(sub);
        breakdownRows.push({
          key: sub.question_id ?? `${mainNum}-${si}`,
          qLabel: si === 0 ? `Q${mainNum}` : "",
          partLabel: (SUB_LETTERS[si] ?? String(si + 1)).toUpperCase(),
          questionText: sub.question_text,
          ...m,
          isMainRow: si === 0,
        });
      });
    } else {
      const m = computeLeafMarks(row);
      breakdownRows.push({
        key: row.question_id ?? idx,
        qLabel: `Q${row.display_label ?? mainNum}`,
        partLabel: "",
        questionText: row.question_text,
        ...m,
        isMainRow: true,
      });
    }
  });

  // Same basis as the Result page: the header score is the sum of the per-part marks the breakdown
  // shows, floored at the stored rule/AI score so no confirmed credit is lost.
  const partScored = breakdownRows.reduce((s, r) => s + Number(r.earned ?? 0), 0);
  const displayScore = breakdownRows.length
    ? Math.max(partScored, ruleScoreNum, aiScore != null ? Number(aiScore) : 0)
    : (aiScore != null ? Math.max(Number(aiScore), ruleScoreNum) : ruleScoreNum);
  const displayPct = (payload?.total_marks ?? 0) > 0
    ? Math.round((displayScore / payload.total_marks) * 1000) / 10
    : Number(payload?.percentage ?? 0);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Button size="small" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}
        onClick={() => navigate(getPastPaperResultPath(purchaseId, 'result'))}>
        Back to Result
      </Button>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {payload?.paper_name || "Paper review"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Question-by-question review
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            {aiScore != null ? (
              <>
                <Tooltip title="AI-adjusted score (semantic matching)">
                  <Chip
                    icon={<AiIcon sx={{ fontSize: 16 }} />}
                    label={`AI Score: ${Number(displayScore).toFixed(1)} / ${Number(payload?.total_marks ?? 0).toFixed(1)}`}
                    color="secondary" sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
                <Chip label={`${Number(displayPct).toFixed(1)}%`} variant="outlined" color="secondary" />
              </>
            ) : (
              <>
                <Chip label={`Score: ${Number(displayScore).toFixed(1)} / ${Number(payload?.total_marks ?? 0).toFixed(1)}`} color="primary" />
                <Chip label={`${Number(displayPct).toFixed(1)}%`} variant="outlined" />
              </>
            )}
            {(aiFeedbackStatus === "pending" || aiFeedbackStatus === "processing") && (
              <Chip
                icon={<CircularProgress size={12} sx={{ color: "#7c3aed" }} />}
                label="AI feedback generating…" size="small"
                sx={{ bgcolor: "#f3f0ff", color: "#7c3aed", border: "1px solid #e0d7ff" }}
              />
            )}
            {aiFeedbackStatus === "completed" && (
              <Chip icon={<AiIcon sx={{ fontSize: 14 }} />} label="AI feedback ready" size="small"
                sx={{ bgcolor: "#f3f0ff", color: "#7c3aed", border: "1px solid #e0d7ff" }} />
            )}
          </Box>

        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 3 }}>
        <Tab value="questions" label="Questions" />
        <Tab value="marks" label="Marks Breakdown" />
        <Tab value="ai" label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AiIcon sx={{ fontSize: 16 }} />
            AI Insights
            {(aiFeedbackStatus === "pending" || aiFeedbackStatus === "processing") && (
              <CircularProgress size={10} sx={{ ml: 0.5, color: "#7c3aed" }} />
            )}
          </Box>
        } />
      </Tabs>

      {tab === "questions" && (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Questions {questions.length > 0 ? `(Total: ${payload?.top_level_count ?? questions.length})` : ""}
          </Typography>

          {questions.length === 0 ? (
            <Alert severity="info">No questions in this review.</Alert>
          ) : (
            questions.map((row, idx) => {
              const isGrouped = String(row.type || row.question_type || "").toLowerCase() === "grouped"
                || (Array.isArray(row.sub_questions) && row.sub_questions.length > 0);
              return isGrouped ? (
                <GroupedReviewCard
                  key={row.question_id ?? idx}
                  row={row}
                  number={idx + 1}
                  aiFeedbackMap={aiFeedbackMap}
                  aiFeedbackStatus={aiFeedbackStatus}
                />
              ) : (
                <QuestionReviewCard
                  key={row.question_id ?? idx}
                  row={row}
                  label={`Q${row.display_label ?? idx + 1}`}
                  aiFeedbackMap={aiFeedbackMap}
                  aiFeedbackStatus={aiFeedbackStatus}
                />
              );
            })
          )}
        </>
      )}

      {tab === "marks" && (
        <MarksBreakdownTable
          rows={breakdownRows}
          totalMarks={payload?.total_marks ?? 0}
          scoredMarks={displayScore}
          title="Marks Breakdown"
          fileName={`${(payload?.paper_name || "paper").replace(/[^\w-]+/g, "_").slice(0, 60)}-marks`}
        />
      )}

      {tab === "ai" && (
        <AiInsightsTab
          aiFeedbackStatus={aiFeedbackStatus}
          aiOverall={aiOverall}
          aiScore={aiScore}
          aiPercentage={aiPercentage}
          totalMarks={payload?.total_marks}
          ruleScore={payload?.score}
        />
      )}
    </Box>
  );
};

export default StudentPaperReview;
